import { Player } from '../entities/Player';
import { PowerUp } from '../entities/PowerUp';
import { AIBot } from '../entities/AIBot';
import { GameConfig, GameState, Position, GamePhase } from '../types/GameTypes';
import { DEBUG_CONFIG } from '../config/debug';

export class GameEngine {
  public players: Player[] = [];
  public powerups: PowerUp[] = [];
  public gameState: GameState = 'lobby';
  public gameTime: number = 90;
  public suddenDeathTime: number = 30;
  public score: number = 0;
  public round: number = 1;
  public config: GameConfig;
  public currentPhase: GamePhase | null = null;
  public debugMode: boolean = DEBUG_CONFIG.enabled;
  
  private gameLoop: NodeJS.Timeout | null = null;
  private powerupSpawnTimer: number = 0;
  private phaseTimer: number = 0;
  private countdownTime: number = 3;

  constructor(config: Partial<GameConfig> = {}) {
    this.config = {
      maxPlayers: 8,
      roundTime: 90,
      suddenDeathTime: 30,
      gravity: 1.2,
      airFriction: 0.995,
      groundFriction: 0.92,
      bounce: 0.8,
      playerRadius: 35,
      touchTolerance: 60,
      powerupSpawnInterval: 60, // 6 seconds at 100ms tick
      maxPowerups: 3,
      healthSystem: true,
      eliminations: true,
      arenaShrinking: false,
      ...config
    };
    this.gameTime = this.config.roundTime;
    this.suddenDeathTime = this.config.suddenDeathTime;
  }

  addPlayer(name: string, color: string, startPosition: Position): Player {
    if (this.players.length >= this.config.maxPlayers) {
      throw new Error(`Maximum ${this.config.maxPlayers} players allowed!`);
    }

    const id = Date.now().toString() + Math.random();
    const player = new Player(id, name, color, startPosition, this.config.playerRadius);
    this.players.push(player);
    return player;
  }

  removePlayer(playerId: string): boolean {
    const index = this.players.findIndex(p => p.id === playerId);
    if (index !== -1) {
      this.players.splice(index, 1);
      return true;
    }
    return false;
  }

  startGame(areaWidth: number, areaHeight: number): void {
    // In debug mode, allow single player with AI bots
    if (this.debugMode && this.players.length === 1) {
      this.addAIBots(areaWidth, areaHeight);
    }
    
    if (this.players.length < 2) {
      throw new Error('Need at least 2 players to start!');
    }

    this.gameState = 'countdown';
    this.countdownTime = this.debugMode && DEBUG_CONFIG.skipLobby ? 1 : 3;
    this.gameTime = this.config.roundTime;
    this.resetPlayers();
    this.startCountdown(areaWidth, areaHeight);
  }

  private startCountdown(areaWidth: number, areaHeight: number): void {
    const countdownLoop = setInterval(() => {
      this.countdownTime--;
      
      if (this.countdownTime <= 0) {
        clearInterval(countdownLoop);
        this.gameState = 'playing';
        this.currentPhase = this.getPhaseForTime(this.gameTime);
        this.startGameLoop(areaWidth, areaHeight);
      }
    }, 1000);
  }

  pauseGame(areaWidth?: number, areaHeight?: number): void {
    if (this.gameState === 'playing') {
      this.stopGameLoop();
      this.gameState = 'lobby'; // Use lobby as paused state
    } else if (this.gameState === 'lobby' && areaWidth && areaHeight) {
      this.startGameLoop(areaWidth, areaHeight);
      this.gameState = 'playing';
    }
  }

  endGame(): string | null {
    this.stopGameLoop();
    this.gameState = 'ended';
    
    // Award points for final standings
    const alivePlayers = this.getAlivePlayers().sort((a, b) => b.score - a.score);
    
    alivePlayers.forEach((player, index) => {
      const placement = index + 1;
      let placementPoints = 0;
      
      switch (placement) {
        case 1: placementPoints = 50; break;
        case 2: placementPoints = 30; break;
        case 3: placementPoints = 20; break;
        default: placementPoints = 10; break;
      }
      
      player.addScore(placementPoints, 'last-standing');
    });
    
    const winner = alivePlayers.length > 0 ? alivePlayers[0] : null;
    
    if (winner) {
      this.score += winner.score;
      return winner.name;
    }
    return null;
  }

  restartGame(areaWidth: number, areaHeight: number): void {
    this.round++;
    this.clearPowerups();
    this.startGame(areaWidth, areaHeight);
  }

  returnToLobby(): void {
    this.stopGameLoop();
    this.gameState = 'lobby';
    this.clearPowerups();
  }

  private startGameLoop(areaWidth: number, areaHeight: number): void {
    this.gameLoop = setInterval(() => {
      this.updatePhysics(areaWidth, areaHeight);
      this.updatePowerups(areaWidth, areaHeight);
      this.updateGamePhase();
      this.checkCollisions();
      this.checkWinConditions();
      
      this.gameTime -= 0.1;
      
      // Transition to sudden death
      if (this.gameTime <= this.suddenDeathTime && this.gameState === 'playing') {
        this.gameState = 'sudden-death';
        this.currentPhase = this.getSuddenDeathPhase();
      }
      
      if (this.gameTime <= 0) {
        this.endGame();
      }
    }, 100);
  }

  private stopGameLoop(): void {
    if (this.gameLoop) {
      clearInterval(this.gameLoop);
      this.gameLoop = null;
    }
  }

  private resetPlayers(): void {
    this.players.forEach(player => {
      player.alive = true;
      player.health = player.maxHealth;
      player.velocity = { vx: 0, vy: 0 };
      player.shielded = false;
      player.shieldTime = 0;
      player.stunned = false;
      player.stunnedTime = 0;
      player.score = 0;
    });
  }

  updatePhysics(areaWidth: number, areaHeight: number): void {
    this.players.forEach(player => {
      player.updatePosition(
        areaWidth, 
        areaHeight, 
        this.getCurrentGravity(), 
        this.config.airFriction, 
        this.config.groundFriction, 
        this.config.bounce
      );
      player.updateDOMElement();
      player.updateVelocityArrow();
      
      // Update AI bots
      if (player instanceof AIBot) {
        player.update(this.players, this.powerups, areaWidth, areaHeight);
      }
    });
  }

  private updatePowerups(areaWidth: number, areaHeight: number): void {
    // Update existing powerups
    this.powerups.forEach((powerup, index) => {
      powerup.update();
      
      // Check for collection
      this.players.forEach(player => {
        if (!player.alive) return;
        
        const distance = player.distanceFrom(powerup.position);
        if (distance < player.radius + 25) {
          if (powerup.collect()) {
            player.applyPowerUp(powerup.type);
            player.addScore(this.getPowerupPoints(powerup.type), 'powerup-collect');
            this.removePowerup(index);
          }
        }
      });
    });
    
    // Spawn new powerups
    this.powerupSpawnTimer++;
    const spawnRate = this.currentPhase?.effects.powerupRate || 1;
    
    if (this.powerupSpawnTimer >= (this.config.powerupSpawnInterval / spawnRate) && 
        this.powerups.length < this.config.maxPowerups) {
      this.spawnPowerup(areaWidth, areaHeight);
      this.powerupSpawnTimer = 0;
    }
  }

  private getPowerupPoints(type: PowerUp['type']): number {
    switch (type) {
      case 'boost': return 5;
      case 'mega-jump': return 5;
      case 'shield': return 8;
      case 'health': return 12;
      case 'gravity-flip': return 15;
      default: return 5;
    }
  }

  private updateGamePhase(): void {
    const newPhase = this.getPhaseForTime(this.gameTime);
    if (newPhase !== this.currentPhase) {
      this.currentPhase = newPhase;
      this.phaseTimer = 0;
    }
    this.phaseTimer++;
  }

  private getPhaseForTime(timeLeft: number): GamePhase {
    const totalTime = this.config.roundTime;
    const timeElapsed = totalTime - timeLeft;
    
    if (timeLeft <= this.suddenDeathTime) {
      return this.getSuddenDeathPhase();
    } else if (timeElapsed < 20) {
      return {
        name: 'Early Game',
        duration: 20,
        description: 'Players get familiar with the arena',
        effects: {
          powerupRate: 0.7,
          gravity: 1.0
        }
      };
    } else if (timeElapsed < 45) {
      return {
        name: 'Mid Game',
        duration: 25,
        description: 'Powerups spawn more frequently',
        effects: {
          powerupRate: 1.2,
          gravity: 1.1
        }
      };
    } else {
      return {
        name: 'Late Game',
        duration: totalTime - 45,
        description: 'Chaos intensifies',
        effects: {
          powerupRate: 1.5,
          gravity: 1.3
        }
      };
    }
  }

  private getSuddenDeathPhase(): GamePhase {
    return {
      name: 'Sudden Death',
      duration: this.suddenDeathTime,
      description: 'Arena becomes dangerous!',
      effects: {
        powerupRate: 2.0,
        gravity: 1.5,
        hazardsActive: true
      }
    };
  }

  private getCurrentGravity(): number {
    return this.config.gravity * (this.currentPhase?.effects.gravity || 1);
  }

  spawnPowerup(areaWidth: number, areaHeight: number): PowerUp {
    const powerup = PowerUp.createRandom(areaWidth, areaHeight, this.powerups);
    this.powerups.push(powerup);
    return powerup;
  }

  removePowerup(index: number): void {
    if (index >= 0 && index < this.powerups.length) {
      const powerup = this.powerups[index];
      powerup.destroy();
      this.powerups.splice(index, 1);
    }
  }

  private clearPowerups(): void {
    this.powerups.forEach(powerup => powerup.destroy());
    this.powerups = [];
  }

  private checkCollisions(): void {
    for (let i = 0; i < this.players.length; i++) {
      for (let j = i + 1; j < this.players.length; j++) {
        if (this.players[i].collideWith(this.players[j])) {
          // Award points for successful hits
          const player1 = this.players[i];
          const player2 = this.players[j];
          
          if (!player1.shielded && !player2.shielded) {
            player1.addScore(2, 'collision');
            player2.addScore(2, 'collision');
          }
        }
      }
    }
  }

  private checkWinConditions(): void {
    const alivePlayers = this.getAlivePlayers();
    
    // Check for eliminations
    this.players.forEach(player => {
      if (!player.alive && player.health <= 0) {
        // Award elimination points to other players
        alivePlayers.forEach(alivePlayer => {
          alivePlayer.addScore(10, 'elimination');
          alivePlayer.eliminations++;
        });
      }
    });
    
    // Award survival points periodically
    if (this.phaseTimer % 50 === 0) { // Every 5 seconds
      alivePlayers.forEach(player => {
        player.addScore(1, 'survival');
      });
    }
    
    // Check for domination (one player with significantly higher score)
    if (alivePlayers.length > 1) {
      const sortedByScore = alivePlayers.sort((a, b) => b.score - a.score);
      const leader = sortedByScore[0];
      const second = sortedByScore[1];
      
      if (leader.score - second.score >= 50) {
        leader.addScore(20, 'domination');
      }
    }
    
    // End game if only one player left
    if (alivePlayers.length <= 1) {
      this.endGame();
    }
  }

  jumpNearestPlayer(position: Position, direction?: { x: number; y: number }): Player | null {
    let nearestPlayer: Player | null = null;
    let nearestDistance = Infinity;
    
    // Find nearest player
    for (const player of this.players) {
      if (!player.alive) continue;
      
      const distance = player.distanceFrom(position);
      if (distance < player.radius + this.config.touchTolerance && distance < nearestDistance) {
        nearestDistance = distance;
        nearestPlayer = player;
      }
    }
    
    if (nearestPlayer) {
      nearestPlayer.jump(direction);
      return nearestPlayer;
    }

    // Fallback: jump any player within extended range
    for (const player of this.players) {
      if (!player.alive) continue;
      const distance = player.distanceFrom(position);
      if (distance < 120) {
        player.jump(direction);
        return player;
      }
    }

    return null;
  }

  getAlivePlayers(): Player[] {
    return this.players.filter(p => p.alive);
  }

  needsPowerupSpawn(): boolean {
    const spawnRate = this.currentPhase?.effects.powerupRate || 1;
    return this.powerupSpawnTimer >= (this.config.powerupSpawnInterval / spawnRate) && 
           this.powerups.length < this.config.maxPowerups;
  }

  resetPowerupTimer(): void {
    this.powerupSpawnTimer = 0;
  }

  getLeaderboard(): Player[] {
    return [...this.players].sort((a, b) => b.score - a.score);
  }

  getGameTimeDisplay(): string {
    const minutes = Math.floor(this.gameTime / 60);
    const seconds = Math.floor(this.gameTime % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  getCountdownTime(): number {
    return this.countdownTime;
  }

  getCurrentPhaseInfo(): { name: string; description: string } | null {
    return this.currentPhase ? {
      name: this.currentPhase.name,
      description: this.currentPhase.description
    } : null;
  }

  // Debug mode methods
  addAIBots(areaWidth: number, areaHeight: number): void {
    if (!this.debugMode) return;
    
    const botsToAdd = Math.min(DEBUG_CONFIG.aiOpponents, this.config.maxPlayers - this.players.length);
    
    for (let i = 0; i < botsToAdd; i++) {
      const startPosition = {
        x: (areaWidth / (botsToAdd + 1)) * (i + 1),
        y: areaHeight / 2
      };
      
      const aiBot = AIBot.createAIBot(i, startPosition);
      this.players.push(aiBot);
    }
  }

  enableDebugMode(): void {
    this.debugMode = true;
    console.log('🐛 Debug mode enabled');
  }

  disableDebugMode(): void {
    this.debugMode = false;
    // Remove AI bots
    this.players = this.players.filter(player => !(player instanceof AIBot));
    console.log('🐛 Debug mode disabled');
  }

  getDebugInfo(): any {
    if (!this.debugMode) return null;
    
    return {
      gameState: this.gameState,
      gameTime: this.gameTime,
      playersCount: this.players.length,
      alivePlayers: this.getAlivePlayers().length,
      powerupsCount: this.powerups.length,
      currentPhase: this.currentPhase?.name,
      players: this.players.map(p => ({
        name: p.name,
        alive: p.alive,
        health: p.health,
        score: p.score,
        position: p.position,
        velocity: p.velocity,
        isAI: p instanceof AIBot,
        personality: p instanceof AIBot ? p.personality : null
      })),
      powerups: this.powerups.map(p => ({
        type: p.type,
        position: p.position,
        rarity: p.getRarity()
      }))
    };
  }
}